// World (points) ↔ screen (CSS pixels) for the Design canvas: screen = world · zoom + pan.
import type { Artboard } from '../model/types';
import type { View } from '../state';

export const ZOOM_MIN = 0.02;
export const ZOOM_MAX = 64;
export const clampZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

export const toScreen = (v: View, x: number, y: number): [number, number] => [
  x * v.zoom + v.panX,
  y * v.zoom + v.panY,
];
export const toWorld = (v: View, sx: number, sy: number): [number, number] => [
  (sx - v.panX) / v.zoom,
  (sy - v.panY) / v.zoom,
];

/** Zoom by `factor` keeping the world point under (sx, sy) fixed. */
export function zoomAt(v: View, sx: number, sy: number, factor: number): View {
  const zoom = clampZoom(v.zoom * factor);
  const [wx, wy] = toWorld(v, sx, sy);
  return { zoom, panX: sx - wx * zoom, panY: sy - wy * zoom };
}

/** A view that fits the artboards' union into a w × h viewport with a margin. */
export function fitView(artboards: Artboard[], w: number, h: number, margin = 40): View {
  if (!artboards.length || w <= 0 || h <= 0) return { zoom: 1, panX: 0, panY: 0 };
  const x0 = Math.min(...artboards.map((a) => a.x)),
    y0 = Math.min(...artboards.map((a) => a.y)),
    x1 = Math.max(...artboards.map((a) => a.x + a.w)),
    y1 = Math.max(...artboards.map((a) => a.y + a.h));
  const zoom = clampZoom(Math.min((w - 2 * margin) / (x1 - x0), (h - 2 * margin) / (y1 - y0)));
  return { zoom, panX: (w - (x1 - x0) * zoom) / 2 - x0 * zoom, panY: (h - (y1 - y0) * zoom) / 2 - y0 * zoom };
}

/** A "nice" ruler step (1, 2, 5 × 10ⁿ) in document units for at least `minPx` screen pixels per step. */
export function rulerStep(unitPerPt: number, zoom: number, minPx = 60): number {
  const unitsPerPx = unitPerPt / zoom;
  const raw = unitsPerPx * minPx;
  const p = 10 ** Math.floor(Math.log10(raw));
  for (const m of [1, 2, 5, 10]) if (m * p >= raw) return m * p;
  return 10 * p;
}
